import { Site } from '@snapchat/mw-common';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { Feature, useFeatureFlags } from './components/FeatureFlags';
import { Slug } from './components/Slug';
import { Config } from './config';
import { cookieSettingsPath } from './helpers/trackingCookies';
import { CookieSettings } from './pages/CookieSettings';
import { NoMatch } from './pages/NoMatch';
import { Search } from './pages/Search';
import { PoliticalAdAsset } from './sites/snap/components/PoliticalAdAsset/PoliticalAdAsset';

export const Routes: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const featureFlags = useFeatureFlags();

  const enableSearchFeature = featureFlags[Feature.ENABLE_SITE_SEARCH] === 'true';

  /**
   * Handle SSR - animate scroll on first client render (uses timeout to allow child nodes to
   * render)
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: this should only run once.
  useEffect(() => {
    if (!location.hash) return;

    const timeout = setTimeout(() => {
      const element = document.getElementById(location.hash.slice(1));
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  /** Listener for client-side same page anchor navigation. Ensures page scrolls to target element. */
  // TODO: switch to useNavigate hook when we upgrade to react-router v6
  // https://reactrouter.com/api/hooks/useNavigate
  useEffect(() => {
    const listenerCleanup = history.listen(location => {
      if (!location.hash) return;

      const element = document.getElementById(location.hash.slice(1));
      element?.scrollIntoView({ behavior: 'smooth' });
    });

    return listenerCleanup;
  }, [history.listen]);

  const renderedRoute = (
    <Switch>
      <Route
        exact
        path={cookieSettingsPath}
        render={() => (
          <CookieSettings
            backgroundColor={Config.theme?.defaultPageBackgroundColor}
            cookieDomain={Config.trackingSettings.cookieDomain}
            deploymentType={Config.deploymentType}
            domainName={Config.domainName}
            isClient={Config.isClient}
          />
        )}
      />
      {enableSearchFeature && <Route exact path="/search" component={Search} />}
      {Config.site === Site.SNAP && (
        <Route path="/political-ads/asset/:assetId" component={PoliticalAdAsset} />
      )}
      <Route path="/404" component={NoMatch} />
      {/* Needs to match all paths, including '' (default path after prefix, i.e. /fr-FR) */}
      <Route path="*" component={Slug} />
    </Switch>
  );

  return renderedRoute;
};
